import React, {useState, useEffect, useCallback, useRef} from 'react';
import {collection, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter, where} from 'firebase/firestore';
import {firestoreProd} from '../../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiUrl } from '../../globalVariables';
import EnterpriseBillingDialog from '../UserPermissions/EnterpriseBillingDialog';
import { getExemptReasonLabel } from '../UserPermissions/enterpriseBillingOptions';
import { describePerLoanForm, perLoanFormFromBilling } from '../UserPermissions/PerLoanPricingFields';

const { REACT_APP_ADMIN_TOKEN } = process.env;
const ADMIN_TOKEN = REACT_APP_ADMIN_TOKEN || '928c6b5d-d111-4cc4-a275-61992e8d35c9';

// Add some CSS for the accordion
const accordionStyles = `
    .accordion-button:focus {
        box-shadow: none;
    }
    .accordion-arrow {
        transition: transform 0.2s ease;
    }
    .accordion-button[aria-expanded="true"] .accordion-arrow {
        transform: rotate(180deg);
    }
    .accordion-button {
        color: #495057 !important;
    }
    .accordion-button:hover {
        background-color: #f8f9fa;
    }
`;

const WhitelistManagementPage = () => {
    const [prodWhitelistEmails, setProdWhitelistEmails] = useState([]);
    const [newProdWhitelistEmail, setNewProdWhitelistEmail] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [prodWhitelistSearch, setProdWhitelistSearch] = useState('');
    const [loadingEmails, setLoadingEmails] = useState(true);
    const [submittingForm, setSubmittingForm] = useState(false);
    const [removingEmailId, setRemovingEmailId] = useState(null);
    
    // New state variables for checkboxes
    const [sendEmail, setSendEmail] = useState(false);
    const [sendScheduleOnboardingEmail, setSendScheduleOnboardingEmail] = useState(false);
    const [makeEnterprise, setMakeEnterprise] = useState(false);
    const [enterpriseBilling, setEnterpriseBilling] = useState(null);
    const [isEnterpriseDialogOpen, setIsEnterpriseDialogOpen] = useState(false);

    const handleMakeEnterpriseChange = (checked) => {
        if (!checked) {
            setMakeEnterprise(false);
            setEnterpriseBilling(null);

            return;
        }

        setIsEnterpriseDialogOpen(true);
    };

    // Input mode state
    const [whitelistMode, setWhitelistMode] = useState('single'); // single | batch | meetings
    const [batchEmails, setBatchEmails] = useState('');
    const [batchProgress, setBatchProgress] = useState(null); // { current, total, succeeded, failed }
    // Pagination state
    const [hasMoreUsers, setHasMoreUsers] = useState(true);
    const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
    const [lastUserDoc, setLastUserDoc] = useState(null);
    const [allUsersLoaded, setAllUsersLoaded] = useState(false);
    
    // UI Pagination state (for displaying 10 users per page)
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage] = useState(10);
    
    // Track the total number of users loaded from Firebase
    const [totalUsersLoaded, setTotalUsersLoaded] = useState(0);

    // Google Meet extraction state
    const [googleMeetUrl, setGoogleMeetUrl] = useState('');
    const [meetingMode, setMeetingMode] = useState('future');
    const [delegatedMeetAccountEmail, setDelegatedMeetAccountEmail] = useState('');
    const [extractingMeetEmails, setExtractingMeetEmails] = useState(false);
    const [meetCandidateEmails, setMeetCandidateEmails] = useState([]);
    const [selectedMeetCandidateEmails, setSelectedMeetCandidateEmails] = useState({});
    const [meetLookupMetadata, setMeetLookupMetadata] = useState(null);

    // Zoom extraction state
    const [loadingZoomMeetings, setLoadingZoomMeetings] = useState(false);
    const [zoomMeetings, setZoomMeetings] = useState([]);
    const [selectedZoomMeetingIds, setSelectedZoomMeetingIds] = useState({});
    const [zoomMeetingsSearch, setZoomMeetingsSearch] = useState('');
    const [loadingZoomEmails, setLoadingZoomEmails] = useState(false);
    const [zoomCandidateEmails, setZoomCandidateEmails] = useState([]);
    const [zoomEditableEmails, setZoomEditableEmails] = useState('');
    const [meetingParticipantsById, setMeetingParticipantsById] = useState({});
    const [meetingParticipantsExpanded, setMeetingParticipantsExpanded] = useState({});
    const [meetingParticipantsLoading, setMeetingParticipantsLoading] = useState({});
    const hasAutoLoadedMeetings = useRef(false);

    /**
     * Loads whitelist emails and users from Firestore.
     * On initial load, fetches whitelist-emails collection and first 25 users.
     * On subsequent loads, fetches next 25 users using pagination.
     * @param {boolean} loadMore - Whether to load more users for pagination
     * @param {boolean} skipLoadingState - Whether to skip setting loading states
     */
    const loadWhitelistEmails = useCallback(async (loadMore = false, skipLoadingState = false) => {
        if (!skipLoadingState) {
            if (loadMore) {
                setLoadingMoreUsers(true);
            } else {
                setLoadingEmails(true);
            }
        }
        
        try {
            if (firestoreProd) {
                if (!loadMore) {
                    // Initial load - get whitelist emails and first 25 users
                    const prodEmailsRef = collection(firestoreProd, 'whitelist-emails');
                    const prodSnapshot = await getDocs(prodEmailsRef);
                    const prodEmails = prodSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Load first 25 users
                    const prodUsersRef = collection(firestoreProd, 'users');
                    const usersQuery = query(
                        prodUsersRef,
                        where('createdAccountOnClient', '==', true),
                        orderBy('createdAt', 'desc'),
                        limit(25)
                    );
                    
                    const prodUsersSnapshot = await getDocs(usersQuery);
                    const prodUsers = prodUsersSnapshot.docs.map(doc => ({
                        id: doc.id,
                        email: doc.data().email,
                        createdAt: doc.data().createdAt
                    }));
                    
                    // Set up pagination state
                    if (prodUsersSnapshot.docs.length > 0) {
                        setLastUserDoc(prodUsersSnapshot.docs[prodUsersSnapshot.docs.length - 1]);
                    }
                    setHasMoreUsers(prodUsersSnapshot.docs.length === 25);
                    setTotalUsersLoaded(prodUsers.length);
                    setAllUsersLoaded(prodUsersSnapshot.docs.length < 25);
                    
                    // Combine whitelist emails with users
                    setProdWhitelistEmails([...prodEmails, ...prodUsers]);
                } else {
                    // Load more users for pagination
                    if (lastUserDoc && hasMoreUsers && !allUsersLoaded) {
                        const prodUsersRef = collection(firestoreProd, 'users');
                        const usersQuery = query(
                            prodUsersRef,
                            where('createdAccountOnClient', '==', true),
                            orderBy('createdAt', 'desc'),
                            startAfter(lastUserDoc),
                            limit(25)
                        );
                        
                        const prodUsersSnapshot = await getDocs(usersQuery);
                        const newUsers = prodUsersSnapshot.docs.map(doc => ({
                            id: doc.id,
                            email: doc.data().email,
                            createdAt: doc.data().createdAt
                        }));
                        
                        if (newUsers.length > 0) {
                            // Update pagination state
                            setLastUserDoc(prodUsersSnapshot.docs[prodUsersSnapshot.docs.length - 1]);
                            setHasMoreUsers(prodUsersSnapshot.docs.length === 25);
                            setTotalUsersLoaded(prev => prev + newUsers.length);
                            setAllUsersLoaded(prodUsersSnapshot.docs.length < 25);
                            
                            // Add new users to existing list
                            setProdWhitelistEmails(prev => [...prev, ...newUsers]);
                        } else {
                            setAllUsersLoaded(true);
                            setHasMoreUsers(false);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error loading whitelist emails:', error);
            setError('Failed to load whitelist emails');
        } finally {
            if (!skipLoadingState) {
                if (loadMore) {
                    setLoadingMoreUsers(false);
                } else {
                    setLoadingEmails(false);
                }
            }
        }
    }, [lastUserDoc, hasMoreUsers, allUsersLoaded]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                if (!firestoreProd) {
                    throw new Error('Firebase is not initialized');
                }
                await loadWhitelistEmails();
            } catch (error) {
                console.error('Error initializing data:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        initializeData();
    }, [loadWhitelistEmails]);

    /**
     * Adds a new whitelist email or domain.
     * Handles sending welcome emails and enterprise setup if requested.
     */
    const addWhitelistEmail = async () => {
        let newEmail = newProdWhitelistEmail.trim().replace(/\s+/g, '');
        const setNewEmail = setNewProdWhitelistEmail;

        if (!newEmail) {
            toast.error('Please enter an email or domain');
            return;
        }

        setSubmittingForm(true);

        if (newEmail.startsWith('@')) {
            newEmail = newEmail.slice(1);
        }

        try {
            // Call the new backend endpoint for enhanced whitelist functionality
            const response = await fetch(`${apiUrl}/admin/add-whitelist-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: newEmail.toLowerCase(),
                    sendEmail: sendEmail,
                    sendScheduleOnboardingEmail: sendScheduleOnboardingEmail,
                    makeEnterprise: makeEnterprise,
                    enterpriseBilling: makeEnterprise ? enterpriseBilling : null,
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.reason || 'Failed to add whitelist email');
            }

            // Reset form fields immediately after successful API call
            setNewEmail('');
            setSendEmail(false);
            setMakeEnterprise(false);
            setEnterpriseBilling(null);
            setSendScheduleOnboardingEmail(false);
            
            // Reset pagination state
            setLastUserDoc(null);
            setHasMoreUsers(true);
            setAllUsersLoaded(false);
            setCurrentPage(1);
            setTotalUsersLoaded(0);
            
            // Reload whitelist data without changing loading state
            await loadWhitelistEmails(false, true);
            
            let successMessage = `Production whitelist email added successfully`;
            if (sendEmail) {
                successMessage += ' and welcome email sent';
            }
            if (makeEnterprise && result.enterpriseCode) {
                successMessage += ` with enterprise code: ${result.enterpriseCode}`;
            }
            
            toast.success(successMessage);

            // await 10s and send-test endpoint
        } catch (error) {
            console.error(`Error adding whitelist email:`, error);
            setError(`Failed to add whitelist email`); 
            toast.error(`Failed to add whitelist email: ${error.message}`);
        } finally {
            setSubmittingForm(false);
        }
    };

    /**
     * Parses a raw string of emails/domains (comma or newline separated) into a clean array.
     */
    const parseBatchEmails = (raw) => {
        return raw
            .split(/[\n,]+/)
            .map(e => e.trim().replace(/\s+/g, '').toLowerCase())
            .filter(e => e.length > 0)
            .map(e => (e.startsWith('@') ? e.slice(1) : e));
    };

    const getEmailDisplayName = (email) => {
        const normalized = String(email || '').trim().toLowerCase();
        if (!normalized.includes('@')) return normalized;
        return normalized.split('@')[0];
    };

    /**
     * Adds multiple whitelist emails/domains sequentially, tracking progress.
     */
    const addBatchWhitelistEmails = async () => {
        const emails = parseBatchEmails(batchEmails);

        if (emails.length === 0) {
            toast.error('Please enter at least one email or domain');
            return;
        }

        setSubmittingForm(true);
        setBatchProgress({ current: 0, total: emails.length, succeeded: 0, failed: 0 });

        let succeeded = 0;
        let failed = 0;
        const failedEmails = [];

        for (let i = 0; i < emails.length; i++) {
            setBatchProgress({ current: i + 1, total: emails.length, succeeded, failed });

            try {
                const response = await fetch(`${apiUrl}/admin/add-whitelist-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: emails[i],
                        sendEmail,
                        sendScheduleOnboardingEmail,
                        makeEnterprise,
                        enterpriseBilling: makeEnterprise ? enterpriseBilling : null,
                    }),
                });

                if (!response.ok) {
                    const result = await response.json();
                    throw new Error(result.reason || 'Failed');
                }

                succeeded++;
            } catch (err) {
                failed++;
                failedEmails.push(emails[i]);
                console.error(`Failed to whitelist ${emails[i]}:`, err);
            }
        }

        setBatchProgress({ current: emails.length, total: emails.length, succeeded, failed });

        // Reset form and reload
        setBatchEmails('');
        setSendEmail(false);
        setMakeEnterprise(false);
        setEnterpriseBilling(null);
        setSendScheduleOnboardingEmail(false);
        setLastUserDoc(null);
        setHasMoreUsers(true);
        setAllUsersLoaded(false);
        setCurrentPage(1);
        setTotalUsersLoaded(0);
        await loadWhitelistEmails(false, true);

        if (failed === 0) {
            toast.success(`Successfully whitelisted ${succeeded} email${succeeded !== 1 ? 's' : ''}`);
        } else if (succeeded === 0) {
            toast.error(`Failed to whitelist all ${failed} email${failed !== 1 ? 's' : ''}`);
        } else {
            toast.warning(`Whitelisted ${succeeded} successfully, ${failed} failed: ${failedEmails.join(', ')}`);
        }

        setBatchProgress(null);
        setSubmittingForm(false);
    };

    /**
     * Extracts participant emails from Google Meet using backend admin route.
     */
    const extractEmailsFromMeet = async () => {
        const trimmedUrl = googleMeetUrl.trim();
        if (!trimmedUrl) {
            toast.error('Please enter a Google Meet link');
            return;
        }

        setExtractingMeetEmails(true);
        setMeetLookupMetadata(null);
        setMeetCandidateEmails([]);
        setSelectedMeetCandidateEmails({});

        try {
            const response = await fetch(`${apiUrl}/admin/extract-google-meet-participants`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    meetUrl: trimmedUrl,
                    mode: meetingMode,
                    delegatedUserEmail: delegatedMeetAccountEmail.trim() || undefined,
                    token: ADMIN_TOKEN,
                }),
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.reason || 'Failed to extract emails from Google Meet');
            }

            const emails = Array.isArray(result.emails) ? result.emails : [];
            setMeetCandidateEmails(emails);
            const selectedMap = {};
            emails.forEach((email) => {
                selectedMap[email] = true;
            });
            setSelectedMeetCandidateEmails(selectedMap);
            setMeetLookupMetadata(result.metadata || null);

            if (emails.length === 0) {
                toast.warning('No whitelistable participant emails found for this meeting');
            } else {
                toast.success(`Loaded ${emails.length} email${emails.length === 1 ? '' : 's'} from Google Meet`);
            }
        } catch (err) {
            console.error('Error extracting emails from Google Meet:', err);
            toast.error(`Failed to extract emails: ${err.message}`);
        } finally {
            setExtractingMeetEmails(false);
        }
    };

    const addEmailsToWhitelist = async (emailsToAdd, sourceLabel = 'selected') => {
        setSubmittingForm(true);
        let succeeded = 0;
        let failed = 0;

        for (let i = 0; i < emailsToAdd.length; i++) {
            const email = emailsToAdd[i];
            try {
                const response = await fetch(`${apiUrl}/admin/add-whitelist-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email,
                        sendEmail,
                        sendScheduleOnboardingEmail,
                        makeEnterprise,
                        enterpriseBilling: makeEnterprise ? enterpriseBilling : null,
                    }),
                });
                if (!response.ok) {
                    const result = await response.json();
                    throw new Error(result?.reason || 'Failed to whitelist email');
                }
                succeeded++;
            } catch (err) {
                failed++;
                console.error(`Failed to whitelist extracted email "${email}"`, err);
            }
        }

        // Reset pagination and refresh list after batch add
        setLastUserDoc(null);
        setHasMoreUsers(true);
        setAllUsersLoaded(false);
        setCurrentPage(1);
        setTotalUsersLoaded(0);
        await loadWhitelistEmails(false, true);

        if (failed === 0) {
            toast.success(`Successfully whitelisted ${succeeded} ${sourceLabel} email${succeeded === 1 ? '' : 's'}`);
        } else {
            toast.warning(`Whitelisted ${succeeded} ${sourceLabel} email${succeeded === 1 ? '' : 's'}, ${failed} failed`);
        }
        setSubmittingForm(false);
    };

    /**
     * Adds all selected extracted Google Meet emails to whitelist.
     */
    const addSelectedMeetEmailsToWhitelist = async () => {
        const selectedEmails = meetCandidateEmails.filter((email) => selectedMeetCandidateEmails[email]);
        if (selectedEmails.length === 0) {
            toast.error('Select at least one extracted email');
            return;
        }

        await addEmailsToWhitelist(selectedEmails, 'Google Meet');
    };

    const loadZoomMeetings = useCallback(async () => {
        setLoadingZoomMeetings(true);
        setZoomMeetings([]);
        setSelectedZoomMeetingIds({});
        setZoomCandidateEmails([]);
        setZoomEditableEmails('');
        setMeetingParticipantsById({});
        setMeetingParticipantsExpanded({});
        setMeetingParticipantsLoading({});

        try {
            const response = await fetch(`${apiUrl}/admin/zoom/list-meetings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: 'account',
                    token: ADMIN_TOKEN,
                }),
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.reason || 'Failed to load Zoom meetings');
            }

            const meetings = Array.isArray(result.meetings) ? result.meetings : [];
            setZoomMeetings(meetings);
            toast.success(`Loaded ${meetings.length} meeting${meetings.length === 1 ? '' : 's'}`);
        } catch (err) {
            console.error('Error loading meetings:', err);
            toast.error(`Failed to load meetings: ${err.message}`);
        } finally {
            setLoadingZoomMeetings(false);
        }
    }, []);

    useEffect(() => {
        if (isLoading || hasAutoLoadedMeetings.current) return;
        hasAutoLoadedMeetings.current = true;
        loadZoomMeetings();
    }, [isLoading, loadZoomMeetings]);

    const loadZoomMeetingEmails = async () => {
        const selectedMeetingUuids = Object.keys(selectedZoomMeetingIds).filter((meetingUuid) => selectedZoomMeetingIds[meetingUuid]);
        if (selectedMeetingUuids.length === 0) {
            toast.error('Please select at least one meeting');
            return;
        }

        setLoadingZoomEmails(true);
        setZoomCandidateEmails([]);
        setZoomEditableEmails('');

        try {
            const emailSet = new Set();
            let failedMeetings = 0;
            for (const meetingUuid of selectedMeetingUuids) {
                try {
                    const response = await fetch(`${apiUrl}/admin/zoom/extract-participants`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            meetingIdentifier: meetingUuid,
                            userId: 'account',
                            token: ADMIN_TOKEN,
                        }),
                    });

                    const result = await response.json();
                    if (!response.ok || !result?.success) {
                        failedMeetings++;
                        continue;
                    }
                    const emails = Array.isArray(result.emails) ? result.emails : [];
                    emails.forEach((email) => {
                        if (email) emailSet.add(email);
                    });
                } catch (error) {
                    failedMeetings++;
                }
            }
            const emails = Array.from(emailSet);
            setZoomCandidateEmails(emails);
            setZoomEditableEmails(emails.join('\n'));

            if (emails.length === 0) {
                toast.warning('No whitelistable participant emails found for the selected meetings');
            } else {
                const failedSuffix = failedMeetings > 0 ? ` (${failedMeetings} meeting${failedMeetings === 1 ? '' : 's'} failed)` : '';
                toast.success(`Loaded ${emails.length} participant email${emails.length === 1 ? '' : 's'} from ${selectedMeetingUuids.length} meeting${selectedMeetingUuids.length === 1 ? '' : 's'}${failedSuffix}`);
            }
        } catch (err) {
            console.error('Error loading Zoom participant emails:', err);
            toast.error(`Failed to load participant emails: ${err.message}`);
        } finally {
            setLoadingZoomEmails(false);
        }
    };

    const addEditedZoomEmailsToWhitelist = async () => {
        const editedEmails = parseBatchEmails(zoomEditableEmails);
        if (editedEmails.length === 0) {
            toast.error('Please add at least one email in the editable list');
            return;
        }
        await addEmailsToWhitelist(editedEmails, 'Calendly');
    };

    const fetchMeetingParticipants = useCallback(async (meetingUuid, showToastOnError = true) => {
        if (Array.isArray(meetingParticipantsById[meetingUuid])) {
            return meetingParticipantsById[meetingUuid];
        }
        if (meetingParticipantsLoading[meetingUuid]) {
            return [];
        }
        setMeetingParticipantsLoading((prev) => ({ ...prev, [meetingUuid]: true }));
        try {
            const response = await fetch(`${apiUrl}/admin/zoom/extract-participants`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    meetingIdentifier: meetingUuid,
                    userId: 'account',
                    token: ADMIN_TOKEN,
                }),
            });
            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.reason || 'Failed to load participants');
            }
            const emails = Array.isArray(result.emails) ? result.emails : [];
            setMeetingParticipantsById((prev) => ({ ...prev, [meetingUuid]: emails }));
            return emails;
        } catch (error) {
            console.error('Error loading meeting participants:', error);
            if (showToastOnError) {
                toast.error(`Failed to load participants: ${error.message}`);
            }
            setMeetingParticipantsById((prev) => ({ ...prev, [meetingUuid]: [] }));
            return [];
        } finally {
            setMeetingParticipantsLoading((prev) => ({ ...prev, [meetingUuid]: false }));
        }
    }, [meetingParticipantsById, meetingParticipantsLoading]);

    const toggleMeetingParticipants = async (meetingUuid) => {
        const currentlyExpanded = !!meetingParticipantsExpanded[meetingUuid];
        if (currentlyExpanded) {
            setMeetingParticipantsExpanded((prev) => ({ ...prev, [meetingUuid]: false }));
            return;
        }

        if (!Array.isArray(meetingParticipantsById[meetingUuid])) {
            await fetchMeetingParticipants(meetingUuid, true);
        }
        setMeetingParticipantsExpanded((prev) => ({ ...prev, [meetingUuid]: true }));
    };

    const removeWhitelistEmail = async (id) => {
        setLoadingEmails(true);
        setRemovingEmailId(id);
        const db = firestoreProd;
        if (!db) return;

        try {
            const emailRef = doc(db, 'whitelist-emails', id);
            await deleteDoc(emailRef);
            // Reset pagination state and reload
            setLastUserDoc(null);
            setHasMoreUsers(true);
            setAllUsersLoaded(false);
            setCurrentPage(1);
            setTotalUsersLoaded(0);
            await loadWhitelistEmails(false, true);
            toast.success(`Production whitelist email removed successfully`);
        } catch (error) {
            console.error(`Error removing whitelist email:`, error);
            setError(`Failed to remove whitelist email`); 
            toast.error(`Failed to remove whitelist email`);
        } finally {
            setLoadingEmails(false);
            setRemovingEmailId(null);
        }
    };

    const filteredProdWhitelist = prodWhitelistEmails.filter(email =>
        email.email.toLowerCase().includes(prodWhitelistSearch.toLowerCase())
    );
    const filteredZoomMeetings = zoomMeetings.filter((meeting) => {
        const query = zoomMeetingsSearch.trim().toLowerCase();
        if (!query) return true;
        const topic = String(meeting.topic || '').toLowerCase();
        const organizer = String(meeting.organizerEmail || '').toLowerCase();
        return topic.includes(query) || organizer.includes(query);
    });
    const todayDateLabel = new Date().toDateString();
    const todayZoomMeetings = filteredZoomMeetings.filter((meeting) => (
        meeting.startTime && new Date(meeting.startTime).toDateString() === todayDateLabel
    ));
    const nonTodayZoomMeetings = filteredZoomMeetings.filter((meeting) => (
        !meeting.startTime || new Date(meeting.startTime).toDateString() !== todayDateLabel
    ));
    const renderMeetingRow = (meeting) => {
        const checked = !!selectedZoomMeetingIds[meeting.uuid];
        const participants = meetingParticipantsById[meeting.uuid];
        const participantsLoaded = Array.isArray(participants);
        const participantsCount = participantsLoaded ? participants.length : null;
        const participantNames = participantsLoaded
            ? participants.map((email) => getEmailDisplayName(email)).filter(Boolean)
            : [];
        const participantsSummary = participantsLoaded
            ? (
                participantsCount === 0
                    ? 'No participants'
                    : `${participantNames.slice(0, 2).join(', ')}${participantsCount > 2 ? ', ...' : ''}`
            )
            : 'Not loaded';
        const isParticipantsExpanded = !!meetingParticipantsExpanded[meeting.uuid];
        const isParticipantsLoading = !!meetingParticipantsLoading[meeting.uuid];
        return (
            <React.Fragment key={`${meeting.uuid}-${meeting.id}`}>
                <tr
                    style={{ cursor: 'pointer' }}
                    className={checked ? 'table-primary' : ''}
                    onClick={() => {
                        setSelectedZoomMeetingIds((prev) => ({
                            ...prev,
                            [meeting.uuid]: !prev[meeting.uuid],
                        }));
                    }}
                >
                    <td>
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={checked}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                setSelectedZoomMeetingIds((prev) => ({
                                    ...prev,
                                    [meeting.uuid]: isChecked,
                                }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </td>
                    <td>
                        <div className="fw-medium">{meeting.topic || '(no topic)'}</div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                            <span className="small text-muted" title={participantsLoaded ? participants.join(', ') : ''}>
                                {isParticipantsLoading
                                    ? 'Loading participants...'
                                    : participantsLoaded
                                        ? participantsSummary
                                        : 'Auto-loading participants...'}
                            </span>
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMeetingParticipants(meeting.uuid);
                                }}
                                disabled={isParticipantsLoading}
                            >
                                {isParticipantsLoading ? 'Loading...' : (isParticipantsExpanded ? 'Hide' : 'Show all')}
                            </button>
                        </div>
                    </td>
                    <td>{meeting.startTime ? new Date(meeting.startTime).toLocaleString() : '-'}</td>
                </tr>
                {isParticipantsExpanded && (
                    <tr className={checked ? 'table-primary' : ''}>
                        <td />
                        <td colSpan={2}>
                            {participantsLoaded && participants.length > 0 ? (
                                <div className="small">
                                    {participants.map((email) => (
                                        <span key={`${meeting.uuid}-${email}`} className="badge bg-light text-dark me-1 mb-1">{email}</span>
                                    ))}
                                </div>
                            ) : (
                                <span className="small text-muted">No participants found for this meeting.</span>
                            )}
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    useEffect(() => {
        if (whitelistMode !== 'meetings') return;
        let cancelled = false;
        const visibleMeetingIds = filteredZoomMeetings.slice(0, 20).map((meeting) => meeting.uuid);

        const loadSummariesGradually = async () => {
            for (const meetingUuid of visibleMeetingIds) {
                if (cancelled) break;
                if (Array.isArray(meetingParticipantsById[meetingUuid]) || meetingParticipantsLoading[meetingUuid]) {
                    continue;
                }
                await fetchMeetingParticipants(meetingUuid, false);
                await new Promise((resolve) => setTimeout(resolve, 250));
            }
        };

        loadSummariesGradually();
        return () => {
            cancelled = true;
        };
    }, [whitelistMode, filteredZoomMeetings, meetingParticipantsById, meetingParticipantsLoading, fetchMeetingParticipants]);

    // Calculate pagination for filtered results
    const totalPages = Math.ceil(filteredProdWhitelist.length / usersPerPage);
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const currentPageUsers = filteredProdWhitelist.slice(startIndex, endIndex);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [prodWhitelistSearch]);

    /**
     * Automatically load more data when user navigates to a page that requires it.
     * This ensures we have enough data loaded to display the current page.
     */
    useEffect(() => {
        // Calculate how many items we need for the current page
        const itemsNeededForCurrentPage = currentPage * usersPerPage;
        
        // Check if we need to load more data
        // We need to load if:
        // 1. The items needed exceed what we have loaded
        // 2. There are more users to load
        // 3. We're not currently loading
        // 4. We're not searching (when searching, we only work with loaded data)
        if (
            itemsNeededForCurrentPage > prodWhitelistEmails.length &&
            hasMoreUsers &&
            !loadingMoreUsers &&
            !allUsersLoaded &&
            prodWhitelistSearch === ''
        ) {
            loadWhitelistEmails(true);
        }
    }, [currentPage, prodWhitelistEmails.length, hasMoreUsers, loadingMoreUsers, allUsersLoaded, prodWhitelistSearch, usersPerPage, loadWhitelistEmails]);

    if (isLoading) {
        return (
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-4">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4 WhitelistManagementPageContainer">
            <style dangerouslySetInnerHTML={{ __html: accordionStyles }} />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <h1>Whitelist Management</h1>

            {/* Instructions Section - Collapsible */}
            <div className="card mb-4">
                <div className="card-header">
                    <h2 className="h5 mb-0">
                        <button 
                            className="accordion-button collapsed p-2 text-decoration-none w-100 d-flex justify-content-between align-items-center border-0"
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#instructionsCollapse" 
                            aria-expanded="false" 
                            aria-controls="instructionsCollapse"
                        >
                            <span>📖 How to Use Whitelist</span>
                            <span className="accordion-arrow">▼</span>
                        </button>
                    </h2>
                </div>
                <div className="collapse" id="instructionsCollapse">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-6">
                                <h6>✅ How to Add Access:</h6>
                                <ol>
                                    <li><strong>Single mode:</strong> Enter one email or domain and click "Add Email or Domain"</li>
                                    <li><strong>Batch mode:</strong> Switch to Batch, paste multiple emails (one per line or comma-separated), then click "Add Emails"</li>
                                    <li><strong>Entire Company:</strong> Enter just the domain (e.g., company.com)</li>
                                    <li><strong>Optional:</strong> Check "Send welcome email" to notify the user(s)</li>
                                    <li><strong>Optional:</strong> Check "Make user enterprise" for enterprise features</li>
                                    <li>Person(s) can now log into the system</li>
                                </ol>
                                
                                <h6 className="mt-4">🔍 How to Remove Access:</h6>
                                <ul>
                                    <li>Use the search box to find the email or domain</li>
                                    <li>Click "Remove" next to the entry</li>
                                    <li>Access is blocked immediately</li>
                                </ul>
                            </div>
                            <div className="col-md-6">
                                <h6>📊 What You'll See:</h6>
                                <ul className="list-unstyled">
                                    <li className="mb-2">
                                        <span className="badge bg-primary me-2">Manual</span>
                                        Entries you added through this page
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-success me-2">Auto</span>
                                        Users who signed up directly
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-success me-2">📧 Yes</span>
                                        Welcome email will be sent
                                    </li>
                                    <li className="mb-2">
                                        <span className="badge bg-primary me-2">🏢 Yes</span>
                                        User has enterprise access
                                    </li>
                                    <li className="mb-2">
                                        <code className="text-success">ABC123...</code>
                                        Enterprise code for automatic activation
                                    </li>
                                </ul>
                                
                                <h6 className="mt-4">💡 Examples:</h6>
                                <ul className="list-unstyled">
                                    <li><strong>Single person:</strong> john.doe@company.com</li>
                                    <li><strong>Whole company:</strong> company.com</li>
                                    <li><strong>University:</strong> university.edu</li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="alert alert-warning mt-3" role="alert">
                            <strong>⚠️ Important:</strong> This controls who can access the live system. Adding a domain allows ALL emails from that domain. Changes happen immediately.
                        </div>
                    </div>
                </div>
            </div>

            {/* Whitelist Emails Section - Prod */}
            <div className="card">
                <div className="card-header">
                    <h2 className="h5 mb-0">Whitelist Emails or Domains - Production</h2>
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        {/* Single / Batch / Meetings mode toggle */}
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="fw-semibold">Mode:</span>
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    className={`btn ${whitelistMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => { setWhitelistMode('single'); setBatchProgress(null); }}
                                    disabled={submittingForm}
                                >
                                    Single
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${whitelistMode === 'batch' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => { setWhitelistMode('batch'); setBatchProgress(null); }}
                                    disabled={submittingForm}
                                >
                                    Batch
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${whitelistMode === 'meetings' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => { setWhitelistMode('meetings'); setBatchProgress(null); }}
                                    disabled={submittingForm}
                                >
                                    Meetings
                                </button>
                            </div>
                            {whitelistMode === 'batch' && (
                                <span className="text-muted small">Enter multiple emails or domains separated by commas or new lines</span>
                            )}
                            {whitelistMode === 'meetings' && (
                                <span className="text-muted small">Select meetings, load all participant emails, then whitelist selected emails.</span>
                            )}
                        </div>

                        {whitelistMode === 'single' ? (
                            /* Single mode */
                            <div className="row g-2 align-items-end">
                                <div className="col-md-8">
                                    <label htmlFor="newProdWhitelistEmail" className="form-label">Enter email or domain to be whitelisted</label>
                                    <input
                                        type="email"
                                        id="newProdWhitelistEmail"
                                        className="form-control"
                                        placeholder="Enter new email or domain to be whitelisted"
                                        value={newProdWhitelistEmail}
                                        onChange={(e) => setNewProdWhitelistEmail(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !submittingForm) {
                                                addWhitelistEmail();
                                            }
                                        }}
                                        disabled={submittingForm}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={() => addWhitelistEmail()}
                                        disabled={submittingForm}
                                    >
                                        {submittingForm ? (
                                            <span>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {sendEmail ? 'Adding & Sending Email...' : 'Adding...'}
                                            </span>
                                        ) : (
                                            'Add Email or Domain'
                                        )}
                                    </button>
                                </div>
                            </div>
                        ) : whitelistMode === 'batch' ? (
                            /* Batch mode */
                            <div className="row g-2 align-items-end">
                                <div className="col-md-8">
                                    <label htmlFor="batchWhitelistEmails" className="form-label">
                                        Enter emails or domains to be whitelisted
                                        {batchEmails && (
                                            <span className="ms-2 badge bg-secondary">
                                                {parseBatchEmails(batchEmails).length} entr{parseBatchEmails(batchEmails).length !== 1 ? 'ies' : 'y'} detected
                                            </span>
                                        )}
                                    </label>
                                    <textarea
                                        id="batchWhitelistEmails"
                                        className="form-control"
                                        rows={5}
                                        placeholder={"john@company.com\njane@company.com\ncompany.com\nanother@example.com"}
                                        value={batchEmails}
                                        onChange={(e) => setBatchEmails(e.target.value)}
                                        disabled={submittingForm}
                                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                    />
                                </div>
                                <div className="col-md-4 d-flex flex-column gap-2">
                                    <button
                                        className="btn btn-primary w-100"
                                        onClick={() => addBatchWhitelistEmails()}
                                        disabled={submittingForm || parseBatchEmails(batchEmails).length === 0}
                                    >
                                        {submittingForm && batchProgress ? (
                                            <span>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {batchProgress.current}/{batchProgress.total}...
                                            </span>
                                        ) : (
                                            `Add ${parseBatchEmails(batchEmails).length > 0 ? parseBatchEmails(batchEmails).length : ''} Email${parseBatchEmails(batchEmails).length !== 1 ? 's' : ''}`
                                        )}
                                    </button>
                                    {submittingForm && batchProgress && (
                                        <div>
                                            <div className="progress" style={{ height: '6px' }}>
                                                <div
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                                                />
                                            </div>
                                            <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.75rem' }}>
                                                <span className="text-success">✓ {batchProgress.succeeded}</span>
                                                {batchProgress.failed > 0 && <span className="text-danger">✗ {batchProgress.failed}</span>}
                                                <span className="text-muted">{batchProgress.current}/{batchProgress.total}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="alert alert-light border mb-0">
                                <div className="small text-muted">
                                    Meetings mode enabled. Use the meetings table below to pick one or more meetings and load participant emails.
                                </div>
                            </div>
                        )}
                        
                        {/* Checkboxes Section */}
                        {whitelistMode !== 'meetings' && (
                        <div className="row mt-3">
                            <div className="col-md-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendEmailCheckbox"
                                        checked={sendEmail}
                                        onChange={(e) => setSendEmail(e.target.checked)}
                                        disabled={submittingForm}
                                    />
                                    <label className="form-check-label" htmlFor="sendEmailCheckbox">
                                        📧 Send this user an invitation email for them to create their account
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendScheduleOnboardingEmailCheckbox"
                                        checked={sendScheduleOnboardingEmail}
                                        onChange={(e) => setSendScheduleOnboardingEmail(e.target.checked)}
                                        disabled={submittingForm}
                                    />
                                    <label className="form-check-label" htmlFor="sendScheduleOnboardingEmailCheckbox">
                                        📧 After account creation send this user an email to schedule onboarding call
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="makeEnterpriseCheckbox"
                                        checked={makeEnterprise}
                                        onChange={(e) => handleMakeEnterpriseChange(e.target.checked)}
                                        disabled={submittingForm}
                                    />
                                    <label className="form-check-label" htmlFor="makeEnterpriseCheckbox">
                                        🏢 Automatically set their organization to enterprise after creation
                                    </label>
                                    {makeEnterprise && enterpriseBilling && (
                                        <div className="form-text">
                                            {enterpriseBilling.billingExempt
                                                ? `Billing exempt · ${getExemptReasonLabel(enterpriseBilling)}`
                                                : describePerLoanForm(perLoanFormFromBilling(enterpriseBilling))}
                                            {' · '}
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 align-baseline"
                                                onClick={() => setIsEnterpriseDialogOpen(true)}
                                                disabled={submittingForm}
                                            >
                                                Change
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-text">
                                    ℹ️ Addy team signup notifications are now automatic when users
                                    actually create accounts in the app.
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Google Meet extraction 
                        <div className="card mt-4 border-info">
                            <div className="card-header bg-light">
                                <h6 className="mb-0">Google Meet to extract participant emails</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-6">
                                        <label htmlFor="googleMeetUrl" className="form-label">Google Meet link</label>
                                        <input
                                            id="googleMeetUrl"
                                            type="text"
                                            className="form-control"
                                            placeholder="https://meet.google.com/abc-defg-hij"
                                            value={googleMeetUrl}
                                            onChange={(e) => setGoogleMeetUrl(e.target.value)}
                                            disabled={extractingMeetEmails || submittingForm}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label htmlFor="meetingMode" className="form-label">Meeting mode</label>
                                        <select
                                            id="meetingMode"
                                            className="form-select"
                                            value={meetingMode}
                                            onChange={(e) => setMeetingMode(e.target.value)}
                                            disabled={extractingMeetEmails || submittingForm}
                                        >
                                            <option value="future">Future meeting (all invitees)</option>
                                            <option value="ongoing">Ongoing meeting (accepted/tentative)</option>
                                        </select>
                                    </div>
                                    <div className="col-md-3">
                                        <button
                                            className="btn btn-outline-primary w-100"
                                            onClick={extractEmailsFromMeet}
                                            disabled={extractingMeetEmails || submittingForm}
                                        >
                                            {extractingMeetEmails ? 'Loading...' : 'Load Emails'}
                                        </button>
                                    </div>
                                </div>

                                <div className="row g-2 mt-1">
                                    <div className="col-md-9">
                                        <label htmlFor="delegatedMeetAccountEmail" className="form-label">
                                            Delegated Google Calendar account email (optional override)
                                        </label>
                                        <input
                                            id="delegatedMeetAccountEmail"
                                            type="email"
                                            className="form-control"
                                            placeholder="sales@yourcompany.com"
                                            value={delegatedMeetAccountEmail}
                                            onChange={(e) => setDelegatedMeetAccountEmail(e.target.value)}
                                            disabled={extractingMeetEmails || submittingForm}
                                        />
                                        <div className="form-text">
                                            Use this if the event is in a specific sales calendar; otherwise backend uses configured delegated account.
                                        </div>
                                    </div>
                                    <div className="col-md-3 d-flex align-items-end">
                                        <button
                                            className="btn btn-success w-100"
                                            onClick={addSelectedMeetEmailsToWhitelist}
                                            disabled={extractingMeetEmails || submittingForm || meetCandidateEmails.length === 0}
                                        >
                                            Add Selected to Whitelist
                                        </button>
                                    </div>
                                </div>

                                {meetLookupMetadata && (
                                    <div className="alert alert-secondary mt-3 mb-2 py-2">
                                        <strong>Matched event:</strong> {meetLookupMetadata.eventSummary || 'Untitled event'}
                                        {meetLookupMetadata.eventStart && (
                                            <span> | {new Date(meetLookupMetadata.eventStart).toLocaleString()}</span>
                                        )}
                                    </div>
                                )}

                                {meetCandidateEmails.length > 0 && (
                                    <div className="mt-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-semibold">
                                                Extracted emails ({meetCandidateEmails.length})
                                            </span>
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => {
                                                        const next = {};
                                                        meetCandidateEmails.forEach((email) => {
                                                            next[email] = true;
                                                        });
                                                        setSelectedMeetCandidateEmails(next);
                                                    }}
                                                    type="button"
                                                >
                                                    Select all
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => setSelectedMeetCandidateEmails({})}
                                                    type="button"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                        <div className="border rounded p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                            {meetCandidateEmails.map((email) => (
                                                <div key={email} className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`meet-email-${email}`}
                                                        checked={!!selectedMeetCandidateEmails[email]}
                                                        onChange={(e) => {
                                                            setSelectedMeetCandidateEmails((prev) => ({
                                                                ...prev,
                                                                [email]: e.target.checked,
                                                            }));
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`meet-email-${email}`}>
                                                        {email}
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        */}

                        {/* Meetings extraction */}
                        {whitelistMode === 'meetings' && (
                        <div className="card mt-4">
                            <div className="card-header bg-light">
                                <h6 className="mb-0">Calendly Meetings</h6>
                            </div>
                            <div className="card-body">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-12">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="form-text mb-0">
                                                Meetings are loaded automatically when this page opens.
                                            </div>
                                        <button
                                            className="btn btn-outline-primary"
                                            onClick={loadZoomMeetings}
                                            disabled={loadingZoomMeetings || loadingZoomEmails || submittingForm}
                                        >
                                            {loadingZoomMeetings ? 'Refreshing...' : 'Refresh Meetings'}
                                        </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <div className="row g-2 align-items-end">
                                        <div className="col-md-6">
                                            <label htmlFor="meetingsSearch" className="form-label">Search meetings</label>
                                            <input
                                                id="meetingsSearch"
                                                type="text"
                                                className="form-control"
                                                placeholder="Search by title or organizer email"
                                                value={zoomMeetingsSearch}
                                                onChange={(e) => setZoomMeetingsSearch(e.target.value)}
                                                disabled={loadingZoomMeetings || loadingZoomEmails || submittingForm}
                                            />
                                        </div>
                                        <div className="col-md-4 d-flex gap-2">
                                            <button
                                                className="btn btn-outline-secondary w-100"
                                                type="button"
                                                onClick={() => {
                                                    const next = {};
                                                    filteredZoomMeetings.forEach((meeting) => {
                                                        next[meeting.uuid] = true;
                                                    });
                                                    setSelectedZoomMeetingIds(next);
                                                }}
                                                disabled={loadingZoomMeetings || loadingZoomEmails || submittingForm || filteredZoomMeetings.length === 0}
                                            >
                                                Select Visible
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row g-2 align-items-end mt-1">
                                        <div className="col-md-10">
                                            <div className="form-text mb-0">
                                                Meetings are grouped automatically: today first, then all others.
                                            </div>
                                        </div>
                                        <div className="col-md-2 d-flex gap-2">
                                            <button
                                                className="btn btn-outline-secondary w-100"
                                                type="button"
                                                onClick={() => setSelectedZoomMeetingIds({})}
                                                disabled={loadingZoomMeetings || loadingZoomEmails || submittingForm}
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>

                                    <hr className="my-2" />

                                    <div className="table-responsive border rounded mt-2" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                        <table className="table table-sm align-middle mb-0">
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '40px' }} />
                                                    <th>Meeting</th>
                                                    <th>Start Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredZoomMeetings.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={3} className="text-center text-muted py-3">
                                                            {loadingZoomMeetings ? 'Loading meetings...' : 'No meetings found'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    <>
                                                        {todayZoomMeetings.map(renderMeetingRow)}
                                                        {todayZoomMeetings.length > 0 && nonTodayZoomMeetings.length > 0 && (
                                                            <tr>
                                                                <td colSpan={3} className="text-center text-muted py-2">
                                                                    --- Other meetings ---
                                                                </td>
                                                            </tr>
                                                        )}
                                                        {nonTodayZoomMeetings.map(renderMeetingRow)}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <div className="form-text mb-0">
                                            Selected: {Object.keys(selectedZoomMeetingIds).filter((meetingUuid) => selectedZoomMeetingIds[meetingUuid]).length}
                                        </div>
                                        <button
                                            className="btn btn-outline-success"
                                            onClick={loadZoomMeetingEmails}
                                            disabled={
                                                loadingZoomEmails ||
                                                submittingForm ||
                                                Object.keys(selectedZoomMeetingIds).filter((meetingUuid) => selectedZoomMeetingIds[meetingUuid]).length === 0
                                            }
                                        >
                                            {loadingZoomEmails ? 'Loading...' : 'Load Emails From Selected'}
                                        </button>
                                    </div>
                                </div>

                                {zoomCandidateEmails.length > 0 && (
                                    <div className="mt-3">
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-8">
                                                <label htmlFor="editableMeetingEmails" className="form-label">
                                                    Editable email list
                                                    <span className="ms-2 badge bg-secondary">
                                                        {parseBatchEmails(zoomEditableEmails).length} entr{parseBatchEmails(zoomEditableEmails).length !== 1 ? 'ies' : 'y'}
                                                    </span>
                                                </label>
                                                <textarea
                                                    id="editableMeetingEmails"
                                                    className="form-control"
                                                    rows={5}
                                                    placeholder={"person1@company.com\nperson2@company.com"}
                                                    value={zoomEditableEmails}
                                                    onChange={(e) => setZoomEditableEmails(e.target.value)}
                                                    disabled={loadingZoomEmails || submittingForm}
                                                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                                                />
                                            </div>
                                            <div className="col-md-4 d-flex flex-column gap-2">
                                                <button
                                                    className="btn btn-primary w-100"
                                                    type="button"
                                                    onClick={addEditedZoomEmailsToWhitelist}
                                                    disabled={loadingZoomEmails || submittingForm || parseBatchEmails(zoomEditableEmails).length === 0}
                                                >
                                                    Add {parseBatchEmails(zoomEditableEmails).length > 0 ? parseBatchEmails(zoomEditableEmails).length : ''} Email{parseBatchEmails(zoomEditableEmails).length !== 1 ? 's' : ''}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}

                        {whitelistMode === 'meetings' && (
                        <div className="row mt-3">
                            <div className="col-md-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendEmailCheckboxMeetings"
                                        checked={sendEmail}
                                        onChange={(e) => setSendEmail(e.target.checked)}
                                        disabled={submittingForm}
                                    />
                                    <label className="form-check-label" htmlFor="sendEmailCheckboxMeetings">
                                        📧 Send this user an invitation email for them to create their account
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendScheduleOnboardingEmailCheckboxMeetings"
                                        checked={sendScheduleOnboardingEmail}
                                        onChange={(e) => setSendScheduleOnboardingEmail(e.target.checked)}
                                        disabled={submittingForm}
                                    />
                                    <label className="form-check-label" htmlFor="sendScheduleOnboardingEmailCheckboxMeetings">
                                        📧 After account creation send this user an email to schedule onboarding call
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="makeEnterpriseCheckboxMeetings"
                                        checked={makeEnterprise}
                                        onChange={(e) => handleMakeEnterpriseChange(e.target.checked)}
                                        disabled={submittingForm}
                                    />
                                    <label className="form-check-label" htmlFor="makeEnterpriseCheckboxMeetings">
                                        🏢 Automatically set their organization to enterprise after creation
                                    </label>
                                    {makeEnterprise && enterpriseBilling && (
                                        <div className="form-text">
                                            {enterpriseBilling.billingExempt
                                                ? `Billing exempt · ${getExemptReasonLabel(enterpriseBilling)}`
                                                : describePerLoanForm(perLoanFormFromBilling(enterpriseBilling))}
                                            {' · '}
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 align-baseline"
                                                onClick={() => setIsEnterpriseDialogOpen(true)}
                                                disabled={submittingForm}
                                            >
                                                Change
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="form-text">
                                    ℹ️ Addy team signup notifications are now automatic when users
                                    actually create accounts in the app.
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search production whitelist..."
                            value={prodWhitelistSearch}
                            onChange={(e) => setProdWhitelistSearch(e.target.value)}
                        />
                    </div>
                    <div className="table-responsive" style={{maxHeight: '300px', overflowY: 'auto'}}>
                        <table className="table">
                            <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                                <tr>
                                    <th>Email/Domain</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPageUsers.map((email) => (
                                    <tr key={email.id}>
                                        <td>{email.email}</td>
                                        <td>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeWhitelistEmail(email.id)}
                                                disabled={loadingEmails || removingEmailId !== null}
                                            >
                                                {removingEmailId === email.id ? 'Removing...' : 'Remove'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination Status */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                            {filteredProdWhitelist.length > 0 && (
                                <span>
                                    Showing {startIndex + 1} to {Math.min(endIndex, filteredProdWhitelist.length)} of {filteredProdWhitelist.length} entries
                                    {prodWhitelistSearch === '' && !allUsersLoaded && hasMoreUsers && (
                                        <span className="ms-2">
                                            ({totalUsersLoaded} users loaded from database)
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        
                        <div className="d-flex gap-2 align-items-center">
                            {/* Loading indicator */}
                            {loadingMoreUsers && (
                                <div className="d-flex align-items-center text-muted">
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    Loading more...
                                </div>
                            )}
                            
                            {/* All users loaded indicator */}
                            {allUsersLoaded && prodWhitelistSearch === '' && (
                                <span className="text-success">
                                    ✅ All users loaded ({totalUsersLoaded} total)
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Page Navigation */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <nav aria-label="Page navigation">
                                <ul className="pagination pagination-sm">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                        >
                                            First
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    
                                    {/* Page Numbers */}
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        
                                        return (
                                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Last
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>
            <EnterpriseBillingDialog
                isOpen={isEnterpriseDialogOpen}
                organizationData={{ name: 'New whitelisted organization' }}
                onClose={() => setIsEnterpriseDialogOpen(false)}
                onActivate={(billingUpdates) => {
                    setEnterpriseBilling(billingUpdates);
                    setMakeEnterprise(true);
                    setIsEnterpriseDialogOpen(false);
                }}
            />
        </div>
    );
};

export default WhitelistManagementPage;
